/**
 * O vocabulário: as palavras que o coletor leva às fontes.
 *
 * É o portão de entrada do funil, e o único ponto irreversível dele. Termo que
 * não está aqui nunca vira linha no banco — não dá para "voltar atrás e olhar
 * de novo" o que nunca foi buscado. Por isso a tela facilita ACRESCENTAR e faz
 * questão de mostrar o custo de tirar: o número `só ele`.
 *
 * Tirar tem dois jeitos. `rejeitado` deixa a palavra na lista, apagada, com a
 * nota do motivo. EXCLUIR (pedido do Pedro, 2026-10-05: "um botão para excluir
 * as palavras que eu não quiser que o sistema use mais, não só desativar") a
 * tira da tabela — e pede confirmação no próprio cartão, que repete o custo:
 * quantas licitações só ela achou. O acervo não muda; o histórico da
 * Configuração guarda quem excluiu.
 *
 * `traz` é quantas licitações do acervo casam o termo. `só ele` é quantas
 * casam ELE E MAIS NENHUM — isto é, quantas sumiriam se ele saísse. Um termo
 * com traz alto e só-ele zero é redundante; um com só-ele alto é insubstituível.
 *
 * O DESENHO (20/10, "reimaginar tudo"): cada termo é um CARTÃO — a palavra, os
 * dois números, o estado num seletor de três botões e o excluir —, numa grade
 * com busca, filtro por estado e ordem. Era uma tabela de seis colunas.
 *
 * AS PEÇAS DO DESIGN SYSTEM (20/10): o campo da palavra nova, a busca, as
 * escolhas (o filtro e o estado de cada palavra), o seletor da ordem, os
 * botões, o excluir (o botão de ícone de perigo), a pílula e os avisos saem de
 * `src/ds`. Em `gestao.module.css` ficou o que é desta tela: a barra de
 * ferramentas, a grade e o cartão da palavra.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ErroApi, type Termo } from "../api/cliente";
import { AreaConfig } from "../componentes/AreaConfig";
import {
  Aviso, Botao, BotaoIcone, Busca, Campo, Escolha, IconeLixeira, Pilula, Seletor, type TomDeEstado,
} from "../ds";
import { useSessao } from "../sessao";
import css from "./gestao.module.css";

const ESTADOS = ["ativo", "candidato", "rejeitado"] as const;
type Estado = (typeof ESTADOS)[number];
// Palavra, no feminino: "Ativa" é a que o sistema busca, "Sugerida" a que
// espera alguém aprovar (o estado `candidato`), "Rejeitada" a que ficou fora.
const NOME: Record<Estado, string> = { ativo: "Ativa", candidato: "Sugerida", rejeitado: "Rejeitada" };
const NO_FILTRO: Record<Estado, string> = { ativo: "Ativas", candidato: "Sugeridas", rejeitado: "Rejeitadas" };
const TOM: Record<Estado, TomDeEstado> = { ativo: "ok", candidato: "atencao", rejeitado: "neutro" };
const ORDENS: { valor: "traz" | "so_ele" | "az"; rotulo: string }[] = [
  { valor: "traz", rotulo: "As que mais trazem" },
  { valor: "so_ele", rotulo: "As insubstituíveis" },
  { valor: "az", rotulo: "De A a Z" },
];

export function Vocabulario() {
  const { podeOperar } = useSessao();
  const cliente = useQueryClient();
  const [filtro, setFiltro] = useState<"" | Estado>("");
  const [busca, setBusca] = useState("");
  const [ordem, setOrdem] = useState<"traz" | "so_ele" | "az">("traz");
  const [novo, setNovo] = useState("");
  // O termo esperando a confirmação da exclusão. Um de cada vez: abrir a
  // pergunta em outro cartão fecha a anterior.
  const [excluindo, setExcluindo] = useState<string | null>(null);

  const { data, isPending, error } = useQuery({
    queryKey: ["termos"],
    queryFn: () => api.termos(),
  });

  const mudar = useMutation({
    mutationFn: (v: { termo: string; estado: Termo["estado"]; nota?: string }) =>
      api.mudaTermo(v.termo, v.estado, v.nota),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ["termos"] });
      void cliente.invalidateQueries({ queryKey: ["historico"] });
    },
  });

  const apagar = useMutation({
    mutationFn: (termo: string) => api.apagaTermo(termo),
    onSuccess: () => {
      setExcluindo(null);
      void cliente.invalidateQueries({ queryKey: ["termos"] });
      void cliente.invalidateQueries({ queryKey: ["historico"] });
    },
  });

  const visiveis = useMemo(() => {
    const q = busca.trim().toLowerCase();
    const lista = (data ?? []).filter(
      (t) => (!filtro || t.estado === filtro) && (!q || t.termo.includes(q)),
    );
    return lista.sort((a, b) =>
      ordem === "az"
        ? a.termo.localeCompare(b.termo, "pt-BR")
        : ordem === "so_ele"
          ? b.so_ele - a.so_ele || b.traz - a.traz
          : b.traz - a.traz || a.termo.localeCompare(b.termo, "pt-BR"),
    );
  }, [data, filtro, busca, ordem]);

  if (isPending || error) {
    return (
      <AreaConfig titulo="Palavras de busca">
        {isPending ? (
          <Aviso simples>Lendo o vocabulário…</Aviso>
        ) : (
          <Aviso tom="erro" simples>{(error as Error).message}</Aviso>
        )}
      </AreaConfig>
    );
  }

  const conta = (e: Estado) => data.filter((t) => t.estado === e).length;
  const ativos = conta("ativo");

  const acrescentar = () => {
    const t = novo.trim().toLowerCase();
    if (!t) return;
    mudar.mutate({ termo: t, estado: "ativo" }, { onSuccess: () => setNovo("") });
  };

  return (
    <AreaConfig
      titulo="Palavras de busca"
      descricao={
        <>
          {ativos} de {data.length} palavras ativas. O sistema só traz dos portais
          as licitações que têm alguma delas: o que não tem nenhuma nunca chega
          aqui, e tirar uma palavra é a única perda que nada depois recupera.
        </>
      }
      acoes={
        podeOperar && (
          <form
            className={css.novoTermo}
            onSubmit={(e) => {
              e.preventDefault();
              acrescentar();
            }}
          >
            <Campo id="novo-termo" className={css.novoCampo} aria-label="Nova palavra de busca"
                   value={novo} spellCheck={false}
                   placeholder="Nova palavra, ex.: processo seletivo"
                   onChange={(e) => setNovo(e.target.value)} />
            <Botao type="submit" variante="primario" disabled={!novo.trim() || mudar.isPending}>
              Acrescentar
            </Botao>
          </form>
        )
      }
    >
      {mudar.error instanceof ErroApi && <Aviso tom="erro" simples>{mudar.error.message}</Aviso>}
      {apagar.error instanceof ErroApi && <Aviso tom="erro" simples>{apagar.error.message}</Aviso>}

      <div className={css.ferramentas}>
        <Busca rotulo="Procurar uma palavra" placeholder="Procurar uma palavra…"
               value={busca} onChange={(e) => setBusca(e.currentTarget.value)} />

        <Escolha
          rotulo="Filtrar por estado"
          valor={filtro}
          aoMudar={setFiltro}
          opcoes={[
            { valor: "" as const, rotulo: "Todas", conta: data.length },
            ...ESTADOS.map((e) => ({ valor: e, rotulo: NO_FILTRO[e], conta: conta(e) })),
          ]}
        />

        <Seletor className={css.direita} rotulo="Ordenar por" valor={ordem} aoMudar={setOrdem}
                 opcoes={ORDENS} />
      </div>

      <Aviso>
        <b>Trouxe</b> é quantas licitações da base têm a palavra. <b>Só por ela</b>{" "}
        é quantas nenhuma outra palavra traria — o que deixaria de ser achado
        se ela saísse.
      </Aviso>

      {visiveis.length === 0 ? (
        <Aviso simples>Nenhuma palavra com esses filtros.</Aviso>
      ) : (
        <ul className={css.grade} aria-label={`${visiveis.length} palavras`}>
          {visiveis.map((t) => (
            <li key={t.termo}>
              <article className={css.termo} data-estado={t.estado}>
                <div className={css.termoCabeca}>
                  <h3>{t.termo}</h3>
                  {podeOperar && (
                    <BotaoIcone pequeno perigo className={css.termoExcluir}
                                rotulo={`Excluir ${t.termo}`}
                                title="Excluir a palavra"
                                aria-expanded={excluindo === t.termo}
                                disabled={apagar.isPending}
                                onClick={() => setExcluindo(excluindo === t.termo ? null : t.termo)}>
                      <IconeLixeira tamanho={15} />
                    </BotaoIcone>
                  )}
                </div>

                <dl className={css.termoNumeros}>
                  <div>
                    <dt>trouxe</dt>
                    <dd>{t.traz}</dd>
                  </div>
                  <div data-zero={t.so_ele === 0}>
                    <dt>só por ela</dt>
                    <dd>{t.so_ele}</dd>
                  </div>
                  <div className={css.termoOrigem}>
                    <dt>origem</dt>
                    <dd>{t.origem}</dd>
                  </div>
                </dl>

                {t.nota && <p className={css.termoNota}>{t.nota}</p>}

                {excluindo === t.termo ? (
                  <div className={css.termoConfirma}>
                    <p>
                      O sistema deixa de buscar por <b>{t.termo}</b>.{" "}
                      {t.traz === 0
                        ? "Ela não trouxe nenhuma licitação até aqui."
                        : `${t.traz === 1
                            ? "A licitação que ela trouxe continua"
                            : `As ${t.traz} licitações que ela trouxe continuam`
                          } no acervo${
                            t.so_ele > 0
                              ? `; ${t.so_ele} ${t.so_ele === 1 ? "foi achada" : "foram achadas"} só por ela.`
                              : "."
                          }`}
                      {t.estado === "ativo" && ativos === 1 && (
                        <> <b>É a última palavra ativa:</b> sem nenhuma, a busca não roda.</>
                      )}
                    </p>
                    <div className={css.termoBotoes}>
                      <Botao variante="perigo" pequeno disabled={apagar.isPending}
                             onClick={() => apagar.mutate(t.termo)}>
                        {apagar.isPending ? "Excluindo…" : "Excluir de vez"}
                      </Botao>
                      <Botao pequeno disabled={apagar.isPending} onClick={() => setExcluindo(null)}>
                        Cancelar
                      </Botao>
                    </div>
                  </div>
                ) : podeOperar ? (
                  <div className={css.termoEstado}>
                    <Escolha
                      pequena
                      rotulo={`Estado de ${t.termo}`}
                      valor={t.estado}
                      aoMudar={(e) => mudar.mutate({ termo: t.termo, estado: e })}
                      desligada={mudar.isPending}
                      opcoes={ESTADOS.map((e) => ({ valor: e, rotulo: NOME[e] }))}
                    />
                  </div>
                ) : (
                  <Pilula tom={TOM[t.estado]}>{NOME[t.estado]}</Pilula>
                )}
              </article>
            </li>
          ))}
        </ul>
      )}

      {visiveis.some((t) => t.traz === 0 && t.estado === "ativo") && (
        <Aviso simples>
          Palavra ativa que <b>trouxe 0</b> não é palavra inútil: ela ainda não
          rodou numa busca. A base só tem o que as palavras antigas trouxeram, e só
          a próxima busca diz quanto ela vale.
        </Aviso>
      )}
    </AreaConfig>
  );
}
